import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  PointerSensor,
  DragOverlay,
  pointerWithin,
  closestCenter,
  MeasuringStrategy,
  useDroppable,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { RankingOption } from "./pollTypes";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface PollRankingFullFormProps {
  options: RankingOption[];
  order: string[];
  onOrderChange: (order: string[]) => void;
}

const sid = (value: string | number) => String(value);

const SortableOption = ({ option }: { option: RankingOption }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sid(option.id!),
    data: { containerId: "ranked" },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="p-3 flex items-center gap-3">
      <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground">
        {option.image_url ? (
          <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={48} height={48} />
        ) : (
          <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
        )}
      </div>
      <div className="flex-1 space-y-1">
        <p className="font-medium leading-none">{option.label}</p>
        {option.description && <p className="text-sm text-muted-foreground">{option.description}</p>}
      </div>
      <button
        {...attributes}
        {...listeners}
        className={cn(
          "p-2 rounded-md border bg-muted/40 hover:bg-muted flex items-center justify-center ml-auto",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring",
        )}
        aria-label={`Drag to reorder ${option.label}`}
        type="button"
      >
        <GripVertical className="w-4 h-4" />
      </button>
    </Card>
  );
};

const OptionPreview = ({ option }: { option: RankingOption }) => (
  <Card className="p-3 flex items-center gap-3 shadow-lg border-2 border-primary">
    <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground">
      {option.image_url ? (
        <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={48} height={48} />
      ) : (
        <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
      )}
    </div>
    <div className="flex-1 space-y-1">
      <p className="font-medium leading-none">{option.label}</p>
      {option.description && <p className="text-sm text-muted-foreground">{option.description}</p>}
    </div>
    <div className="p-2 rounded-md border bg-muted/40">
      <GripVertical className="w-4 h-4" />
    </div>
  </Card>
);

const PlaceholderDropZone = ({ containerId }: { containerId: string }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${containerId}-placeholder`,
    data: { type: "placeholder", containerId },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex items-center justify-center h-20 rounded-lg border-2 border-dashed transition-colors",
        isOver ? "border-primary bg-primary/10" : "border-muted-foreground/30",
      )}
    >
      <p className="text-sm text-muted-foreground">Drop options here to rank</p>
    </div>
  );
};

export const PollRankingFullForm = ({ options, order, onOrderChange }: PollRankingFullFormProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const rankedIds = useMemo(() => {
    const seen = new Set<string>();
    return order.filter((id) => {
      if (seen.has(id)) return false;
      if (!options.some((opt) => sid(opt.id!) === id)) return false;
      seen.add(id);
      return true;
    });
  }, [options, order]);

  const normalizedOrder = useMemo(() => {
    const missingOptions = options.filter((option) => !rankedIds.includes(sid(option.id!)));
    return [...rankedIds, ...missingOptions.map((option) => sid(option.id!))];
  }, [options, rankedIds]);

  useEffect(() => {
    if (normalizedOrder.length !== order.length || normalizedOrder.some((id, idx) => id !== order[idx])) {
      onOrderChange(normalizedOrder);
    }
  }, [normalizedOrder, onOrderChange, order]);

  const orderedOptions = useMemo(
    () =>
      normalizedOrder
        .map((id) => options.find((option) => sid(option.id!) === id))
        .filter((option): option is RankingOption => Boolean(option)),
    [normalizedOrder, options],
  );

  const { setNodeRef: setRankedRef, isOver: isOverRanked } = useDroppable({
    id: "ranked-container",
    data: { type: "container", containerId: "ranked" },
  });

  const activeOption = activeId ? options.find((opt) => sid(opt.id!) === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const collisionDetectionStrategy = (args: Parameters<typeof pointerWithin>[0]) => {
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) return pointerHits;
    return closestCenter(args);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const isContainerTarget = overId === "ranked-container" || overId === "ranked-placeholder" || overId === "ranked";
    const oldIndex = normalizedOrder.indexOf(activeId);
    const newIndex = isContainerTarget ? normalizedOrder.length - 1 : normalizedOrder.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) return;

    onOrderChange(arrayMove(normalizedOrder, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div data-no-drag="true" className="space-y-6 touch-none">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Rank options</p>
            <span className="text-xs text-muted-foreground">Drag to reorder</span>
          </div>
          <div
            ref={setRankedRef}
            className={cn(
              "rounded-lg border-2 border-border p-3 min-h-[120px] bg-background transition-colors",
              isOverRanked && "border-success bg-success/10",
            )}
          >
            <SortableContext
              id="ranked"
              items={orderedOptions.map((option) => sid(option.id!))}
              strategy={verticalListSortingStrategy}
            >
              {orderedOptions.length === 0 ? (
                <PlaceholderDropZone containerId="ranked" />
              ) : (
                <div className="space-y-2">
                  {orderedOptions.map((option) => (
                    <SortableOption key={option.id} option={option} />
                  ))}
                </div>
              )}
            </SortableContext>
          </div>
        </div>
      </div>

      {createPortal(
        <DragOverlay dropAnimation={null} zIndex={1000} className="pointer-events-none">
          {activeOption && <OptionPreview option={activeOption} />}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};
